import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { filtersToSearchParams, parseSort, searchParamsToFilters } from "./urlFilters";

const NO_EXPLICIT_ALL = [];

/**
 * Estado de filtros de uma tela guardado na URL (voltar de um detalhe restaura a
 * visão; link compartilhável). URL sem parâmetros aplica o padrão da tela.
 * `schema` e `explicitAll` devem ser constantes de módulo.
 */
export const useUrlColumnFilters = ({ schema, explicitAll = NO_EXPLICIT_ALL, getDefaultFilters }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const search = searchParams.toString();
    // `sort` só vale para quem chega por link: lido uma vez, não entra no schema de filtros.
    const initialSortingRef = useRef(null);
    if (initialSortingRef.current === null) initialSortingRef.current = parseSort(searchParams);
    const parsed = useMemo(() => searchParamsToFilters(new URLSearchParams(search), schema), [search, schema]);
    const getDefaultsRef = useRef(getDefaultFilters);
    getDefaultsRef.current = getDefaultFilters;

    const filters = useMemo(
        () => (parsed.hasAny ? parsed.filters : getDefaultsRef.current()),
        [parsed]
    );
    const stateRef = useRef({ filters, q: parsed.q });
    stateRef.current = { filters, q: parsed.q };

    const write = useCallback((nextFilters, nextQ) => {
        const known = nextFilters.filter((filter) => schema[filter.id]);
        setSearchParams(filtersToSearchParams(known, schema, { q: nextQ, explicitAll }), { replace: true });
    }, [setSearchParams, schema, explicitAll]);

    useEffect(() => {
        if (!parsed.hasAny) write(getDefaultsRef.current(), "");
    }, [parsed.hasAny, write]);

    const setFilters = useCallback((updater) => {
        const current = stateRef.current;
        const next = typeof updater === "function" ? updater(current.filters) : updater;
        write(next, current.q);
    }, [write]);

    const setSearch = useCallback((term, { clear = [] } = {}) => {
        const current = stateRef.current;
        write(current.filters.filter((filter) => !clear.includes(filter.id)), String(term ?? "").trim());
    }, [write]);

    const clearAll = useCallback(() => write([], ""), [write]);

    return { filters, q: parsed.q, setFilters, setSearch, clearAll, initialSorting: initialSortingRef.current };
};
